import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User as FirebaseUser } from 'firebase/auth';
import TermsOfServiceModal from './TermsOfServiceModal';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  forceLogin?: boolean; // Không cho phép đóng modal khi true
  firebaseUser?: FirebaseUser | null; // Firebase user nếu đã xác thực
  onLogin?: (name: string, phone: string, address?: string) => Promise<boolean>; // Custom login handler
  showBackButton?: boolean; // Hiển thị nút quay lại
  onBack?: () => void; // Handler cho nút quay lại
}

const LoginModal: React.FC<LoginModalProps> = ({ 
  isOpen, 
  onClose, 
  forceLogin = false,
  firebaseUser = null,
  onLogin,
  showBackButton = false,
  onBack
}) => {
  const { login, isLoading, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
  });
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      return;
    }

    let success = false;

    if (onLogin) {
      // Use custom login handler
      const phone = firebaseUser?.phoneNumber || '';
      success = await onLogin(formData.name.trim(), phone, undefined);
    } else {
      // Use default login
      success = await login(
        formData.name.trim(),
        '', // phone để trống, backend sẽ tự generate
        undefined // address không cần thiết
      );
    }

    if (success) {
      onClose();
      setFormData({ name: '' });
      setTermsAccepted(false);
    }
  };

  const handleClose = () => {
    // Không cho phép đóng modal khi forceLogin = true
    if (forceLogin) {
      return;
    }
    
    onClose();
    setFormData({ name: '' });
    setTermsAccepted(false);
    clearError();
  };

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTerms(false);
    // Submit the form automatically after accepting terms if name is filled
    setTimeout(() => {
      if (formData.name.trim()) {
        const form = document.querySelector('.login-form') as HTMLFormElement;
        if (form) {
          form.requestSubmit();
        }
      }
    }, 100);
  };

  const handleTermsDecline = () => {
    setShowTerms(false);
    // Don't set termsAccepted to true, user needs to accept to proceed
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={forceLogin ? undefined : handleClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <div className="login-modal-header">
          <h2>Đăng nhập KILOBA HUB</h2>
          {showBackButton && (
            <button 
              className="login-modal-back"
              onClick={onBack}
              disabled={isLoading}
            >
              ←
            </button>
          )}
          {!forceLogin && !showBackButton && (
            <button 
              className="login-modal-close"
              onClick={handleClose}
              disabled={isLoading}
            >
              ×
            </button>
          )}
        </div>
        
        <div className="login-modal-body">
          {firebaseUser && (
            <div className="firebase-verification-status">
              <p className="verification-success">
                ✓ Số điện thoại đã được xác thực: {firebaseUser.phoneNumber}
              </p>
            </div>
          )}
          
          <p className="login-modal-description">
            {firebaseUser 
              ? 'Vui lòng nhập thông tin để hoàn tất đăng nhập'
              : forceLogin 
                ? 'Bạn cần đăng nhập để truy cập trò chơi này'
                : 'Vui lòng nhập thông tin để bắt đầu trải nghiệm'
            }
          </p>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="name">Họ và tên *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nhập họ và tên"
                required
                disabled={isLoading}
              />
            </div>
            
            {/* Terms of Service acceptance */}
            
            
            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading || !formData.name.trim()}
            >
              {isLoading ? 'Đang đăng nhập...' : 'Đăng nhập'}
            </button>
          </form>
        </div>

        {/* Terms of Service Modal */}
        <TermsOfServiceModal
          isOpen={showTerms}
          onClose={() => setShowTerms(false)}
          onAccept={handleTermsAccept}
          onDecline={handleTermsDecline}
        />
      </div>
    </div>
  );
};

export default LoginModal;
